import { useState, type FormEvent } from "react";
import {
  createMonitoringSilence,
  expireMonitoringSilence,
  type AuthSession,
  type MonitoringRule,
  type MonitoringSilence,
} from "../api";
import { useI18n } from "../i18n";
import {
  ManagementDialog,
  ManagementMessages,
  managementError,
} from "../managementUI";
import { Note } from "./ui";

export type SilenceAction =
  | { kind: "create"; alertName?: string }
  | { kind: "expire"; silence: MonitoringSilence };
export function SilenceDialog({
  action,
  rules,
  session,
  onClose,
  onSuccess,
}: {
  action: SilenceAction;
  rules: MonitoringRule[];
  session: AuthSession;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}) {
  const { t } = useI18n();
  const [name, setName] = useState(
    action.kind === "create" ? action.alertName || "" : "",
  );
  const [seconds, setSeconds] = useState(3600);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!reason.trim()) return;
    setBusy(true);
    setError("");
    try {
      if (action.kind === "create")
        await createMonitoringSilence(
          session.csrf_token,
          reason.trim(),
          name,
          seconds,
        );
      else
        await expireMonitoringSilence(
          session.csrf_token,
          reason.trim(),
          action.silence.silence_id,
        );
      await onSuccess();
      onClose();
    } catch (caught) {
      setError(managementError(caught, t));
    } finally {
      setBusy(false);
    }
  }
  const names = [
    ...new Set([...rules.map((r) => r.name), ...(name ? [name] : [])]),
  ];
  return (
    <ManagementDialog
      title={
        action.kind === "create"
          ? t("创建告警静默", "Create alert silence")
          : t("结束告警静默", "End alert silence")
      }
      onClose={onClose}
      disabled={busy}
    >
      <ManagementMessages error={error} />
      <Note>
        {action.kind === "create"
          ? t(
              "仅作用于当前数据中心的指定规则。静默期间停止通知，监控和告警计算继续运行。",
              "Applies only to the selected rule in this DC. Notifications are muted; monitoring and rule evaluation continue.",
            )
          : t(
              "结束后，符合通知策略的告警可以恢复通知。",
              "Eligible alerts may resume notifications after this silence ends.",
            )}
      </Note>
      <form className="monitor-form" onSubmit={submit}>
        {action.kind === "create" ? (
          <>
            <label>
              {t("告警规则", "Alert rule")}
              <select
                required
                value={name}
                disabled={busy}
                onChange={(e) => setName(e.target.value)}
              >
                <option value="">{t("请选择规则", "Select a rule")}</option>
                {names.map((n) => (
                  <option key={n}>{n}</option>
                ))}
              </select>
            </label>
            <label>
              {t("持续时间", "Duration")}
              <select
                value={seconds}
                disabled={busy}
                onChange={(e) => setSeconds(Number(e.target.value))}
              >
                <option value={300}>{t("5分钟", "5 minutes")}</option>
                <option value={1800}>{t("30分钟", "30 minutes")}</option>
                <option value={3600}>{t("1小时", "1 hour")}</option>
                <option value={14400}>{t("4小时", "4 hours")}</option>
                <option value={86400}>{t("24小时", "24 hours")}</option>
              </select>
            </label>
          </>
        ) : (
          <p>
            {action.silence.matchers.find((m) => m.name === "alertname")
              ?.value || t("当前DC全部告警", "All alerts in this DC")}
          </p>
        )}
        <label>
          {t("操作原因", "Reason")}
          <textarea
            required
            maxLength={512}
            value={reason}
            disabled={busy}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
        <div className="dialog-actions">
          <button
            type="button"
            className="secondary-button"
            disabled={busy}
            onClick={onClose}
          >
            {t("取消", "Cancel")}
          </button>
          <button
            type="submit"
            className="primary-button"
            disabled={
              busy || !reason.trim() || (action.kind === "create" && !name)
            }
          >
            {busy ? t("提交中…", "Submitting…") : t("确认", "Confirm")}
          </button>
        </div>
      </form>
    </ManagementDialog>
  );
}
